function createSlideshow({
  slideshowSelector,
  slideSelector,
  config = {
    autoPlay: 6500,
    desktopAttraction: 0.01,
    mobileAttraction: 0.025,
    friction: 0.28,
  },
  debug = false,
}) {
  let slideshowElement = null;
  let flickity = null;
  let isMobile = window.innerWidth < 720;
  let cleanupControls = () => {};
  let cleanupFixes = () => {};
  let playbackController = null;
  let resizeHandlerAttached = false;

  /* -------------------------------------------------------------------------- */
  /* Config                                                                     */
  /* -------------------------------------------------------------------------- */
  function getConfig() {
    return {
      // Behavior
      autoPlay: config.autoPlay,
      // autoPlay: config.autoPlay,
      pauseAutoPlayOnHover: false,
      wrapAround: true,
      selectedAttraction: isMobile ? config.mobileAttraction : config.desktopAttraction,
      friction: config.friction,
      dragThreshold: 50,
      // Setup
      cellSelector: slideSelector,
      accessibility: false,
      // Cell position
      contain: false,
      cellAlign: "center",
      percentPosition: false,
      // UI
      prevNextButtons: !isMobile,
      pageDots: true,
    };
  }

  function getSlideshowElement() {
    slideshowElement = document.querySelector(slideshowSelector);
  }

  /* -------------------------------------------------------------------------- */
  /* Playback Controller                                                        */
  /* -------------------------------------------------------------------------- */
  function createPlaybackController(pageDots) {
    let selectedIndex = -1;
    let isPlaying = true;

    function markPlaying(dot) {
      if (dot) dot.removeAttribute("data-state");
    }

    function markPaused(dot) {
      if (dot) dot.setAttribute("data-state", "is-paused");
    }

    function setToPause() {
      if (selectedIndex > -1) {
        markPlaying(pageDots.children[selectedIndex]);
      }

      selectedIndex = flickity.selectedIndex;
      isPlaying = false;

      markPaused(pageDots.children[selectedIndex]);
      flickity.stopPlayer();
    }

    function setToResume() {
      markPlaying(pageDots.children[selectedIndex]);
      isPlaying = true;
      flickity.playPlayer();
    }

    function toggleCurrent(dot) {
      if (isPlaying) {
        flickity.stopPlayer();
        isPlaying = false;
        markPaused(dot);
      } else {
        flickity.playPlayer();
        isPlaying = true;
        markPlaying(dot);
      }
    }

    return {
      setToPause,
      setToResume,
      toggleCurrent,
      getSelectedIndex: () => selectedIndex,
      getIsPlaying: () => isPlaying,
    };
  }

  /* -------------------------------------------------------------------------- */
  /* Accessibility                                                              */
  /* -------------------------------------------------------------------------- */
  function enhanceDotsAccessibility() {
    const dots = slideshowElement.querySelectorAll(".flickity-page-dots .dot");

    dots.forEach((dot, index) => {
      dot.setAttribute("tabindex", "0");
      dot.setAttribute("role", "button");
      dot.setAttribute("aria-label", `Go to slide ${index + 1}`);
    });
  }

  function enhanceSlideAccessibility() {
    const focusablesMapping = [];

    function setTabIndex() {
      const slides = Array.from(slideshowElement.querySelectorAll(".slideshow_cell"));
      slides.forEach((slide) => {
        const focusables = Array.from(slide.querySelectorAll("a, button")).filter(
          (el) => el.getAttribute("tabindex") !== "-1",
        );
        focusablesMapping.push(focusables);
      });
    }
    setTabIndex();

    const slideshowViewport = document.querySelector(".flickity-viewport");

    function listenToFocusin() {
      slideshowViewport.addEventListener(
        "focusin",
        function (e) {
          e.preventDefault();
          playbackController.setToPause();

          // const event = new KeyboardEvent("keydown", {
          //   key: "Tab",
          //   code: "Tab",
          //   bubbles: true,
          // });
          // slideshowViewport.dispatchEvent(event);
        },
        { once: true },
      );
    }
    listenToFocusin();

    function isFocusable(el) {
      if (el.disabled || el.getAttribute("tabindex") === "-1") return false;
      if (el.offsetParent === null) return false; // not visible

      return el.matches("a[href], button, input, select, textarea, [tabindex]");
    }

    slideshowViewport.addEventListener("keydown", function (e) {
      console.log("tab");
      if (e.key !== "Tab") return;

      e.preventDefault();

      const slideIndex = flickity.selectedIndex;
      const currentSlideFocusables = focusablesMapping[slideIndex];

      const focusedElIndex = currentSlideFocusables.indexOf(document.activeElement);
      console.log({ slideIndex, focusedElIndex });

      if (e.shiftKey) {
        if (focusedElIndex > 0) {
          currentSlideFocusables[focusedElIndex - 1].focus({ preventScroll: true });
        } else {
          const prevSlideIndex = slideIndex - 1;
          console.log({ prevSlideIndex });
          if (prevSlideIndex < 0) {
            console.log("no more prev slide");

            const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
              acceptNode: (el) => (isFocusable(el) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
            });

            walker.currentNode = currentSlideFocusables[focusedElIndex];
            const prevFocusable = walker.previousNode();
            if (prevFocusable) {
              prevFocusable.focus({ preventScroll: true });
            } else {
              document.body.focus();
            }

            slideshowViewport.blur();
            listenToFocusin();
            return;
          }

          flickity.select(prevSlideIndex);
          const prevSlideFocusables = focusablesMapping[prevSlideIndex];
          if (prevSlideFocusables[prevSlideFocusables.length - 1]) {
            prevSlideFocusables[prevSlideFocusables.length - 1].focus({ preventScroll: true });
          }
        }
      } else {
        if (focusedElIndex < currentSlideFocusables.length - 1) {
          currentSlideFocusables[focusedElIndex + 1].focus({ preventScroll: true });
        } else {
          const nextSlideIndex = slideIndex + 1;
          console.log({ nextSlideIndex });
          if (nextSlideIndex >= focusablesMapping.length) {
            console.log("no more next slide");
            slideshowViewport.blur();
            const prevButton = slideshowElement.querySelector(".flickity-prev-next-button.previous");
            prevButton.focus({ preventScroll: true });
            listenToFocusin();
            return;
          }

          flickity.select(nextSlideIndex);
          if (focusablesMapping[nextSlideIndex][0]) {
            focusablesMapping[nextSlideIndex][0].focus({ preventScroll: true });
          }
        }
      }
    });
  }

  function setupKeyboardAutoplayStop() {
    let focusIndex = 0;

    const focusable = slideshowElement.querySelectorAll("button, a");
    // focusable.forEach((el) => {
    //   el.addEventListener("focus", () => {
    //     console.log("[kimi2]", "focusable", el);
    //     const slide = el.closest(slideSelector);
    //     flickity.select(Number(slide.dataset.index), false, true);
    //   });
    // });

    function handleKeydown(e) {
      // e.preventDefault();
      if (playbackController.getIsPlaying()) {
        playbackController.setToPause();
        console.log("kimi2", "setToPause");
      }

      if (e.key === "Tab") {
        const slide = e.target.closest(slideSelector);
        flickity.select(Number(slide.dataset.index), false, true);
      }
    }

    slideshowElement.addEventListener("keydown", handleKeydown);

    return () => slideshowElement.removeEventListener("keydown", handleKeydown);
  }

  function setupFocusSync() {
    function handleFocusIn(e) {
      // if (playbackController.getIsPlaying()) {
      //   playbackController.setToPause();
      //   console.log("kimi2", "setToPause");
      // }

      const target = e.target;

      // const slide = target.closest(slideSelector);
      // if (!slide) return;
      // console.log("kimi2", Number(slide.dataset.index));

      // flickity.select(Number(slide.dataset.index), false, true);

      // const cell = flickity.getCell(slide);
      // const slideIndex = flickity.getCellSlideIndex(cell);
      // if (slideIndex === -1) return;
      // flickity.select(slideIndex, true, true);
      // flickity.once("select", (index) => console.log("kimi2", index));

      /*
      const target = e.target;

      const slide = target.closest(slideSelector);
      if (!slide) return;

      // 🔥 Prevent native scroll
      if (typeof target.focus === "function") {
        target.focus({ preventScroll: true });
      }

      const slideIndex = flickity.getCellElements().indexOf(slide);
      if (slideIndex === -1) return;

      if (slideIndex !== flickity.selectedIndex) {
        flickity.select(slideIndex, true, true);
        flickity.once("select", function (index) {
          flickity.resize();
          console.log("kimi2", index);
        });
      }

      playbackController.setToPause();
      */
    }

    slideshowElement.addEventListener("focusin", handleFocusIn);

    return () => slideshowElement.removeEventListener("focusin", handleFocusIn);
  }

  /* -------------------------------------------------------------------------- */
  /* Controls                                                                   */
  /* -------------------------------------------------------------------------- */
  function setupDots(pageDots) {
    function handleClick(event) {
      const dot = event.target.closest(".dot");
      if (!dot) return;

      const newIndex = flickity.selectedIndex;
      const prevIndex = playbackController.getSelectedIndex();

      if (newIndex === prevIndex) {
        playbackController.toggleCurrent(dot);
      } else {
        playbackController.setToPause();
      }
    }

    pageDots.addEventListener("click", handleClick);
    return () => pageDots.removeEventListener("click", handleClick);
  }

  function setupArrows() {
    const buttons = document.querySelectorAll(`${slideshowSelector} .flickity-button`);

    buttons.forEach((btn) => btn.addEventListener("click", playbackController.setToPause));

    return () => buttons.forEach((btn) => btn.removeEventListener("click", playbackController.setToPause));
  }

  function setupWheel() {
    let deltaXAccumulated = 0;
    let gestureTimeout = null;
    let hasTriggeredThisGesture = false;

    const GESTURE_END_DELAY = 120; // how long until we consider gesture finished
    const THRESHOLD = 20; // total movement required to trigger

    function handleWheel(e) {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) return;

      e.preventDefault();

      deltaXAccumulated += e.deltaX;

      clearTimeout(gestureTimeout);
      gestureTimeout = setTimeout(() => {
        deltaXAccumulated = 0;
        hasTriggeredThisGesture = false;
      }, GESTURE_END_DELAY);

      if (hasTriggeredThisGesture) return;
      if (Math.abs(deltaXAccumulated) < THRESHOLD) return;

      hasTriggeredThisGesture = true;

      if (deltaXAccumulated > 0) {
        flickity.next();
      } else {
        flickity.previous();
      }

      playbackController.setToPause();
    }

    slideshowElement.addEventListener("wheel", handleWheel, { passive: false });

    return () => slideshowElement.removeEventListener("wheel", handleWheel);
  }

  function setupHoverBehavior() {
    let isSettled = true;

    function handleSettle() {
      isSettled = true;
    }

    function handleDragStart() {
      isSettled = false;
    }

    function handleMouseEnter() {
      if (!isSettled) return;
      playbackController.setToPause();
    }

    function handleMouseLeave() {
      if (!isSettled) return;
      if (!debug) playbackController.setToResume();
    }

    flickity.on("settle", handleSettle);
    flickity.on("dragStart", handleDragStart);

    slideshowElement.addEventListener("mouseenter", handleMouseEnter);
    slideshowElement.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      flickity.off("settle", handleSettle);
      flickity.off("dragStart", handleDragStart);

      slideshowElement.removeEventListener("mouseenter", handleMouseEnter);
      slideshowElement.removeEventListener("mouseleave", handleMouseLeave);
    };
  }

  function setupControls() {
    const pageDots = slideshowElement.querySelector(".flickity-page-dots");

    playbackController = createPlaybackController(pageDots);

    flickity.on("dragEnd", playbackController.setToPause);

    const cleanups = [
      setupDots(pageDots),
      setupArrows(),
      setupWheel(),
      setupHoverBehavior(),
      // setupKeyboardAutoplayStop(),
      // setupFocusSync(),
    ];

    return () => {
      cleanups.forEach((cleanup) => cleanup());
      flickity.off("dragEnd", playbackController.setToPause);
    };
  }

  function setupFlickityFixes() {
    let windowSize = window.innerWidth > 1600 ? 1600 : window.innerWidth;

    // For fixing an issue on mobile where the slideshow pauses when the user touches the screen
    // without directly interacting with slideshow controls (e.g., buttons or slide swipes).
    function handlePointerDown() {
      if (playbackController.getIsPlaying()) {
        flickity.playPlayer();
      }
    }

    // For fixing slide stacking issue that occurs during window resizing
    function handleSettleResizeFix() {
      const currentSize = window.innerWidth > 1600 ? 1600 : window.innerWidth;

      if (currentSize !== windowSize) {
        windowSize = currentSize;
        flickity.resize();
      }
    }

    flickity.on("pointerDown", handlePointerDown);
    flickity.on("settle", handleSettleResizeFix);

    return () => {
      flickity.off("pointerDown", handlePointerDown);
      flickity.off("settle", handleSettleResizeFix);
    };
  }

  /* -------------------------------------------------------------------------- */
  /* Lazy Loading                                                               */
  /* -------------------------------------------------------------------------- */
  function setupLazyLoading() {
    function loadImage(picture) {
      if (picture.dataset.loaded) return;

      picture.querySelectorAll("source").forEach((source) => (source.srcset = source.dataset.srcset));

      const img = picture.querySelector("img");
      if (img) img.src = img.dataset.src;

      picture.dataset.loaded = "true";
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const curr = entry.target;
          const prev = curr.previousElementSibling || curr.parentNode.lastElementChild;
          const next = curr.nextElementSibling || curr.parentNode.firstElementChild;

          [prev, curr, next].forEach((cell) => {
            if (!cell) return;
            cell.querySelectorAll(".lazy-picture").forEach(loadImage);
          });

          observer.unobserve(curr);
        });
      },
      {
        root: slideshowElement,
        threshold: [0.5, 1],
      },
    );

    document.querySelectorAll(slideSelector).forEach((slide) => observer.observe(slide));
  }

  /* -------------------------------------------------------------------------- */
  /* Resize                                                                     */
  /* -------------------------------------------------------------------------- */
  function handleResize() {
    const currentIsMobile = window.innerWidth < 720;

    if (currentIsMobile !== isMobile) {
      isMobile = currentIsMobile;
      destroy();
      init();
      flickity.playPlayer();
    }
  }

  /* -------------------------------------------------------------------------- */
  /* Lifecycle                                                                  */
  /* -------------------------------------------------------------------------- */
  function initFlickity() {
    flickity = new Flickity(slideshowElement, getConfig());
  }

  function init() {
    getSlideshowElement();
    initFlickity();
    // enhanceDotsAccessibility();
    // enhanceSlideAccessibility();

    cleanupControls = setupControls();
    cleanupFixes = setupFlickityFixes();
    setupLazyLoading();

    flickity.stopPlayer();

    if (!resizeHandlerAttached) {
      window.addEventListener("resize", handleResize);
      resizeHandlerAttached = true;
    }
  }

  function destroy() {
    cleanupControls();
    cleanupFixes();
    flickity.destroy();
    flickity = null;
    playbackController = null;
  }

  return {
    init,
    startAutoPlay: () => flickity.playPlayer(),
    pause: () => flickity.stopPlayer(),
    setToPause: () => playbackController.setToPause(),
    insert: (element, index) => {
      flickity.insert([element], index);

      // Manually append to the flickity element first so it matches the compound selector
      // const slider = slideshowElement.querySelector(".flickity-slider");
      // const slides = flickity.getCellElements();
      // const refNode = slides[index] || null;
      // slider.insertBefore(element, refNode);

      // flickity.reloadCells();
      // flickity.resize();
      // flickity.reposition();
    },
  };
}

module.exports = createSlideshow;
